import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  TablePagination,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Visibility,
  GetApp,
  Warning,
  CheckCircle,
  Cancel,
  Refresh,
  Edit,
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAdmin } from '../context/AdminContext';
import { jsPDF } from 'jspdf';
import inspectionReportLogo from '../assets/images/R-Allys-Logo-2104.png';

const loadTrimmedImageData = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Unable to initialize image canvas context.'));
          return;
        }

        context.drawImage(image, 0, 0);
        const { data, width, height } = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );

        const bgR = data[0];
        const bgG = data[1];
        const bgB = data[2];
        const bgA = data[3];
        const tolerance = 12;

        let left = width;
        let right = -1;
        let top = height;
        let bottom = -1;

        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const pixelIndex = (y * width + x) * 4;
            const r = data[pixelIndex];
            const g = data[pixelIndex + 1];
            const b = data[pixelIndex + 2];
            const a = data[pixelIndex + 3];

            const isDifferentFromBackground =
              Math.abs(r - bgR) > tolerance ||
              Math.abs(g - bgG) > tolerance ||
              Math.abs(b - bgB) > tolerance ||
              Math.abs(a - bgA) > tolerance;

            if (!isDifferentFromBackground) {
              continue;
            }

            if (x < left) {
              left = x;
            }
            if (x > right) {
              right = x;
            }
            if (y < top) {
              top = y;
            }
            if (y > bottom) {
              bottom = y;
            }
          }
        }

        if (right < left || bottom < top) {
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            width: canvas.width,
            height: canvas.height,
          });
          return;
        }

        const trimmedWidth = right - left + 1;
        const trimmedHeight = bottom - top + 1;
        const trimmedCanvas = document.createElement('canvas');
        trimmedCanvas.width = trimmedWidth;
        trimmedCanvas.height = trimmedHeight;

        const trimmedContext = trimmedCanvas.getContext('2d');
        if (!trimmedContext) {
          reject(new Error('Unable to initialize trimmed image canvas context.'));
          return;
        }

        trimmedContext.drawImage(
          canvas,
          left,
          top,
          trimmedWidth,
          trimmedHeight,
          0,
          0,
          trimmedWidth,
          trimmedHeight
        );

        resolve({
          dataUrl: trimmedCanvas.toDataURL('image/png'),
          width: trimmedWidth,
          height: trimmedHeight,
        });
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => {
      reject(new Error('Failed to load inspection report logo image.'));
    };

    image.src = src;
  });

const INSPECTION_QUESTIONS = {
  q1: 'Check Vehicle Lights (head lamps, indicators, license plate lights, brake lights)',
  q2: 'Check Windscreen integrity (cracks/chips)',
  q3: 'Check Windshield Wipers / Washers (front and rear)',
  q4: 'Visual Inspection for Exterior',
  q5: 'Check Service Sticker (on windscreen) for scheduled service date/mileage',
  q6: 'Leaks under the vehicle',
  q7: 'Check inside the engine compartment for any leaks and loose items',
  q8: 'Check Oil Level',
  q9: 'Check Washer Fluid Level',
  q10: 'Check Coolant Level',
  q11: 'Check Power Steering Fluid Level',
  q12: 'Start Engine and Check Transmission Fluid',
  q13: 'Check Tires for Wear and Pressure, inc. spare (Manufacturers specification pressure)',
  q14: 'Check Heater / Defroster / Air conditioner',
  q15: 'Check Headlights / Turn Signal Lights / Hazard Lights / Tail Lights / Reverse Lights',
  q16: 'Check Vehicles for any Damage/s',
  q17: 'Check First Aid Kit',
  q18: 'Check water for Passengers',
  q19: 'Check Wheel chocks, Warning Signs, Jack and Wheel spanner',
  q20: 'Check LED Torch (wind-up)',
};

const formatStatusLabel = (status, fallback = 'Pending') => {
  const normalized = String(status || '').trim();

  if (!normalized) {
    return fallback;
  }

  return normalized
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const InspectionsList = () => {
  const { user, dashboardUsers } = useAdmin();
  const location = useLocation();
  const [inspections, setInspections] = useState([]);
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHasFailures, setFilterHasFailures] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [updateDate, setUpdateDate] = useState('');
  const [updateRemark, setUpdateRemark] = useState('');

  const isGenericAdminLabel = (value) => {
    const normalized = String(value || '').trim();
    return !normalized || /^(admin|admin user|dashboard user)$/i.test(normalized);
  };

  const getNamedDashboardUser = (identity) => {
    const normalizedIdentity = String(identity || '').trim().toLowerCase();
    if (!normalizedIdentity) {
      return null;
    }

    return (dashboardUsers || []).find((dashboardUser) => {
      const userId = String(dashboardUser?.uid || dashboardUser?.id || '').trim().toLowerCase();
      const userEmail = String(dashboardUser?.email || '').trim().toLowerCase();
      return userId === normalizedIdentity || userEmail === normalizedIdentity;
    }) || null;
  };

  const resolveDisplayName = (value) => {
    const text = String(value || '').trim();
    return isGenericAdminLabel(text) ? '' : text;
  };

  const resolveAdminApprovalName = (currentUser) => {
    const directName = resolveDisplayName(currentUser?.displayName) || resolveDisplayName(currentUser?.name);
    if (directName) {
      return directName;
    }

    const namedUserByUid = getNamedDashboardUser(currentUser?.uid);
    const namedUserByEmail = getNamedDashboardUser(currentUser?.email);
    const resolvedName =
      resolveDisplayName(namedUserByUid?.name) ||
      resolveDisplayName(namedUserByUid?.displayName) ||
      resolveDisplayName(namedUserByEmail?.name) ||
      resolveDisplayName(namedUserByEmail?.displayName);

    return resolvedName || 'Admin';
  };

  const resolveInspectionApproverName = (inspection) => {
    const directName =
      resolveDisplayName(inspection?.approvedByName) ||
      resolveDisplayName(inspection?.approvedBy);
    if (directName) {
      return directName;
    }

    const namedUserByUid = getNamedDashboardUser(inspection?.approvedByUid);
    const namedUserByEmail = getNamedDashboardUser(inspection?.approvedByEmail);
    const resolvedName =
      resolveDisplayName(namedUserByUid?.name) ||
      resolveDisplayName(namedUserByUid?.displayName) ||
      resolveDisplayName(namedUserByEmail?.name) ||
      resolveDisplayName(namedUserByEmail?.displayName);

    return resolvedName || (inspection?.status === 'approved' ? 'Admin' : 'Not recorded');
  };

  const currentAdminName = resolveAdminApprovalName(user);

  const fetchInspections = async () => {
    try {
      const q = query(collection(db, 'inspections'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setInspections(data);
      setFilteredInspections(data);
    } catch (e) {
      console.error('Failed to fetch inspections', e);
    }
  };

  useEffect(() => {
    fetchInspections();
  }, []);

  // Handle opening specific inspection from notification
  useEffect(() => {
    if (location.state?.openInspectionId && inspections.length > 0) {
      const inspectionToOpen = inspections.find(i => i.id === location.state.openInspectionId);
      if (inspectionToOpen) {
        handleOpen(inspectionToOpen);
        // Clear the state so it doesn't reopen on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, inspections]);

  useEffect(() => {
    let filtered = [...inspections];

    if (filterStatus !== 'all') {
      filtered = filtered.filter(i => i.status === filterStatus);
    }

    if (filterHasFailures === 'yes') {
      filtered = filtered.filter(i => i.hasFailures === true);
    } else if (filterHasFailures === 'no') {
      filtered = filtered.filter(i => !i.hasFailures);
    }

    setFilteredInspections(filtered);
    setPage(0);
  }, [filterStatus, filterHasFailures, inspections]);

  const handleOpen = (row) => {
    setSelected(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const updateInspectionStatus = async (inspectionId, newStatus) => {
    try {
      const now = Timestamp.now();
      const updatePayload = {
        status: newStatus,
        updatedAt: now,
      };

      if (newStatus === 'approved') {
        updatePayload.approvedBy = currentAdminName;
        updatePayload.approvedByName = currentAdminName;
        updatePayload.approvedByEmail = user?.email || '';
        updatePayload.approvedByUid = user?.uid || '';
        updatePayload.approvedAt = now;
      }

      await updateDoc(doc(db, 'inspections', inspectionId), updatePayload);
      await fetchInspections();
      handleClose();
    } catch (error) {
      console.error('Error updating inspection:', error);
      alert('Failed to update inspection status. Please try again.');
    }
  };

  const handleEditOpen = (questionKey) => {
    setEditItem(questionKey);
    setUpdateDate(new Date().toISOString().split('T')[0]);
    setUpdateRemark('');
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditItem(null);
    setUpdateDate('');
    setUpdateRemark('');
  };

  const handleUpdateFailedItem = async () => {
    if (!selected || !editItem || !updateDate || !updateRemark.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const updatedResults = { ...selected.inspectionResults };
      updatedResults[editItem] = true; // Change from fail to pass

      // Create or update the itemUpdates field to track admin changes
      const itemUpdates = selected.itemUpdates || {};
      itemUpdates[editItem] = {
        previousStatus: 'fail',
        newStatus: 'pass',
        updateDate: updateDate,
        remark: updateRemark,
        updatedBy: 'Admin',
        updatedAt: Timestamp.now(),
      };

      // Recalculate failed count
      const newFailedCount = Object.values(updatedResults).filter(v => v === false).length;
      const hasFailures = newFailedCount > 0;

      await updateDoc(doc(db, 'inspections', selected.id), {
        inspectionResults: updatedResults,
        itemUpdates: itemUpdates,
        failedCount: newFailedCount,
        hasFailures: hasFailures,
        updatedAt: Timestamp.now(),
      });

      await fetchInspections();
      handleEditClose();
      // Update the selected item to reflect changes
      setSelected({
        ...selected,
        inspectionResults: updatedResults,
        itemUpdates: itemUpdates,
        failedCount: newFailedCount,
        hasFailures: hasFailures,
      });
    } catch (error) {
      console.error('Error updating inspection item:', error);
      alert('Failed to update inspection item. Please try again.');
    }
  };

  const exportPdf = async (inspection) => {
    const doc = new jsPDF({
      unit: 'pt',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 38;
    const footerHeight = 28;
    const bodyBottom = pageHeight - margin - footerHeight;
    const contentWidth = pageWidth - margin * 2;

    const palette = {
      primary: [221, 36, 36],
      dark: [24, 28, 33],
      muted: [105, 112, 122],
      border: [221, 226, 232],
      light: [246, 248, 251],
      success: [46, 125, 50],
      warning: [245, 124, 0],
      danger: [198, 40, 40],
      neutral: [117, 117, 117],
    };

    const toDate = (value) => {
      if (!value) {
        return null;
      }

      if (value instanceof Date) {
        return value;
      }

      if (typeof value.toDate === 'function') {
        return value.toDate();
      }

      if (typeof value.seconds === 'number') {
        return new Date(value.seconds * 1000);
      }

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const toDisplayText = (value, fallback = 'N/A') => {
      const text = String(value ?? '').trim();
      return text || fallback;
    };

    const toSingleLine = (value, fallback = 'N/A', maxLength = 56) => {
      const text = toDisplayText(value, fallback).replace(/\s+/g, ' ');
      if (text.length <= maxLength) {
        return text;
      }

      return `${text.slice(0, maxLength - 3)}...`;
    };

    const formatDateTime = (value, fallback = 'N/A') => {
      const date = toDate(value);
      if (!date) {
        return fallback;
      }

      return date.toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    };

    const formatDateForFileName = (value) => {
      const date = toDate(value) || new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const normalizeResult = (value) => {
      if (value === true || String(value).toLowerCase() === 'pass') {
        return 'pass';
      }

      if (value === false || String(value).toLowerCase() === 'fail') {
        return 'fail';
      }

      if (value === null || value === undefined) {
        return 'na';
      }

      const normalized = String(value).trim().toLowerCase();
      if (normalized === 'na' || normalized === 'n/a') {
        return 'na';
      }

      return 'na';
    };

    const getPriorityLabel = (priorityValue) => {
      const normalized = String(priorityValue || '').trim().toLowerCase();
      if (!normalized) {
        return 'Low';
      }
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const getPriorityColor = (priorityLabel) => {
      const normalized = String(priorityLabel || '').trim().toLowerCase();

      if (normalized === 'high') {
        return palette.danger;
      }

      if (normalized === 'medium') {
        return palette.warning;
      }

      if (normalized === 'low') {
        return palette.success;
      }

      return palette.neutral;
    };

    const questionEntries = Object.entries(INSPECTION_QUESTIONS).sort(([a], [b]) => {
      const leftNumber = Number.parseInt(String(a).replace('q', ''), 10);
      const rightNumber = Number.parseInt(String(b).replace('q', ''), 10);
      return leftNumber - rightNumber;
    });

    const results = inspection.inspectionResults || {};
    const analyzedResults = questionEntries.map(([key, question]) => {
      const rawValue = results[key];
      const result = normalizeResult(rawValue);
      const comment =
        inspection.failedItemComments?.[key] ||
        inspection.itemComments?.[key] ||
        inspection.itemUpdates?.[key]?.remark ||
        '';
      const priority = getPriorityLabel(inspection.failedItemPriority?.[key] || 'low');

      return {
        key,
        question,
        rawValue,
        result,
        comment: toDisplayText(comment, ''),
        priority,
      };
    });

    const totalQuestions = analyzedResults.length;
    const passedCount = analyzedResults.filter((item) => item.result === 'pass').length;
    const failedCount = analyzedResults.filter((item) => item.result === 'fail').length;
    const naCount = analyzedResults.filter((item) => item.result === 'na').length;
    const answeredCount = analyzedResults.filter((item) => item.rawValue !== undefined).length;
    const completionPercent = totalQuestions > 0
      ? ((answeredCount / totalQuestions) * 100).toFixed(0)
      : '0';
    const scorePercent = totalQuestions > 0
      ? ((passedCount / totalQuestions) * 100).toFixed(1)
      : '0.0';
    const flaggedItems = analyzedResults.filter((item) => item.result === 'fail');
    const actionsCount = flaggedItems.length;
    const isComplete = answeredCount === totalQuestions;

    const preparedBy = toDisplayText(
      inspection.preparedBy || inspection.driverName,
      'Unspecified'
    );
    const approvedBy = toDisplayText(
      resolveInspectionApproverName(inspection),
      inspection.status === 'approved' ? 'Admin' : 'Not recorded'
    );
    const approvedAt = inspection.approvedAt || (inspection.status === 'approved' ? inspection.updatedAt : null);
    const locationAddress = toDisplayText(
      inspection.location?.address,
      'Location not available'
    );
    const conductedAt =
      inspection.conductedOn ||
      inspection.createdAt ||
      inspection.updatedAt ||
      null;
    const baseActionDate = toDate(inspection.createdAt || inspection.conductedOn) || new Date();
    const actionDueDate = new Date(baseActionDate.getTime() + 24 * 60 * 60 * 1000);
    const reportGeneratedAt = new Date();
    const reportId = toDisplayText(inspection.id || inspection.inspectionId || inspection.taskId, 'N/A');

    let logoImage = null;
    try {
      logoImage = await loadTrimmedImageData(inspectionReportLogo);
    } catch (error) {
      console.warn('Failed to load inspection PDF logo image:', error);
    }

    const drawLogo = (x, y, maxWidth = 160, maxHeight = 56) => {
      if (!logoImage?.dataUrl || !logoImage.width || !logoImage.height) {
        return { width: 0, height: 0 };
      }

      const widthScale = maxWidth / logoImage.width;
      const heightScale = maxHeight / logoImage.height;
      const scale = Math.min(widthScale, heightScale);
      const logoWidth = logoImage.width * scale;
      const logoHeight = logoImage.height * scale;

      doc.addImage(logoImage.dataUrl, 'PNG', x, y, logoWidth, logoHeight);

      return { width: logoWidth, height: logoHeight };
    };

    const drawRightBadge = ({
      rightX,
      y,
      label,
      fillColor,
      textColor = [255, 255, 255],
      fontSize = 8,
      horizontalPadding = 8,
      height = 16,
    }) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
      const badgeWidth = doc.getTextWidth(label) + horizontalPadding * 2;
      const x = rightX - badgeWidth;

      doc.setFillColor(...fillColor);
      doc.roundedRect(x, y, badgeWidth, height, 8, 8, 'F');
      doc.setTextColor(...textColor);
      doc.text(label, x + badgeWidth / 2, y + 11, { align: 'center' });
      doc.setTextColor(...palette.dark);

      return badgeWidth;
    };

    const drawCenteredBadge = ({ x, y, width, label, tone }) => {
      const badgeHeight = 16;
      const maxBadgeWidth = Math.max(44, width - 12);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);

      const proposedBadgeWidth = doc.getTextWidth(label) + 16;
      const badgeWidth = Math.min(maxBadgeWidth, proposedBadgeWidth);
      const badgeX = x + (width - badgeWidth) / 2;
      const badgeY = y + (Math.max(24, badgeHeight) - badgeHeight) / 2;

      if (tone === 'pass') {
        doc.setFillColor(...palette.success);
        doc.setTextColor(255, 255, 255);
        doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 8, 8, 'F');
      } else if (tone === 'fail') {
        doc.setFillColor(...palette.danger);
        doc.setTextColor(255, 255, 255);
        doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 8, 8, 'F');
      } else if (tone === 'priority') {
        doc.setFillColor(...palette.warning);
        doc.setTextColor(255, 255, 255);
        doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 8, 8, 'F');
      } else {
        doc.setFillColor(245, 246, 248);
        doc.setDrawColor(...palette.border);
        doc.setTextColor(...palette.neutral);
        doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 8, 8, 'FD');
      }

      doc.text(label, badgeX + badgeWidth / 2, badgeY + 11, { align: 'center' });
      doc.setTextColor(...palette.dark);
    };

    let y = 0;
    let hasStartedPages = false;

    const drawPageHeader = (sectionTitle) => {
      const headerTop = margin;

      drawLogo(margin, headerTop + 2);

      doc.setDrawColor(...palette.border);
      doc.setLineWidth(0.8);
      doc.line(margin, headerTop + 64, pageWidth - margin, headerTop + 64);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...palette.primary);
      doc.text(sectionTitle, pageWidth - margin, headerTop + 16, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...palette.muted);
      doc.text(`Report ID: ${reportId}`, pageWidth - margin, headerTop + 32, {
        align: 'right',
      });
      doc.text(
        `Generated: ${formatDateTime(reportGeneratedAt)}`,
        pageWidth - margin,
        headerTop + 46,
        { align: 'right' }
      );

      return headerTop + 82;
    };

    const startPage = (sectionTitle) => {
      if (hasStartedPages) {
        doc.addPage();
      }
      hasStartedPages = true;
      y = drawPageHeader(sectionTitle);
    };

    const ensureSpace = (requiredHeight, sectionTitle, onNewPage) => {
      if (y + requiredHeight <= bodyBottom) {
        return;
      }

      startPage(sectionTitle);

      if (typeof onNewPage === 'function') {
        onNewPage();
      }
    };

    const drawSectionHeading = (title, subtitle = '') => {
      ensureSpace(46, title);

      doc.setFillColor(...palette.light);
      doc.roundedRect(margin, y, contentWidth, 32, 6, 6, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...palette.dark);
      doc.text(title, margin + 12, y + 21);

      if (subtitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...palette.muted);
        doc.text(subtitle, pageWidth - margin - 12, y + 21, { align: 'right' });
      }

      y += 44;
    };

    startPage('Inspection Executive Summary');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(25);
    doc.setTextColor(...palette.dark);
    doc.text("R Ally's Vehicle Inspection Checklist", margin, y + 6);

    drawRightBadge({
      rightX: pageWidth - margin,
      y: y - 12,
      label: isComplete ? 'COMPLETE' : 'INCOMPLETE',
      fillColor: isComplete ? palette.success : palette.warning,
      fontSize: 9,
      horizontalPadding: 12,
      height: 20,
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...palette.muted);
    doc.text('Tours & Transfers - Professional Inspection Report', margin, y + 24);
    y += 40;

    const metricCards = [
      {
        label: 'Completion',
        value: `${answeredCount}/${totalQuestions} (${completionPercent}%)`,
        accent: palette.primary,
      },
      {
        label: 'Inspection Score',
        value: `${passedCount}/${totalQuestions} (${scorePercent}%)`,
        accent: palette.success,
      },
      {
        label: 'Flagged Items',
        value: String(failedCount),
        accent: palette.danger,
      },
      {
        label: 'N/A Checks',
        value: String(naCount),
        accent: palette.neutral,
      },
    ];

    const cardGap = 10;
    const cardHeight = 70;
    const cardWidth = (contentWidth - cardGap * (metricCards.length - 1)) / metricCards.length;
    ensureSpace(cardHeight + 14, 'Inspection Executive Summary');

    metricCards.forEach((card, index) => {
      const cardX = margin + index * (cardWidth + cardGap);
      const cardY = y;

      doc.setDrawColor(...palette.border);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 6, 6, 'FD');

      doc.setFillColor(...card.accent);
      doc.rect(cardX, cardY, cardWidth, 4, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...palette.muted);
      doc.text(card.label, cardX + 10, cardY + 23);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...palette.dark);
      const valueLines = doc.splitTextToSize(card.value, cardWidth - 20);
      doc.text(valueLines, cardX + 10, cardY + 42);
    });
    y += cardHeight + 14;

    drawSectionHeading('Inspection Details');

    const detailsLeft = [
      ['Driver', toSingleLine(inspection.driverName || inspection.userName, 'Unknown')],
      ['Submitted (By Driver)', toSingleLine(preparedBy, 'Unspecified')],
      ['Vehicle', toSingleLine(inspection.vehicleName || inspection.vehicleModel, 'Unknown')],
      ['Plate number', toSingleLine(inspection.plateNumber || inspection.vehicleRego, 'Not provided')],
    ];
    const detailsRight = [
      ['Conducted on', formatDateTime(conductedAt, 'Not recorded')],
      ['Inspection type', toSingleLine(inspection.inspectionType, 'General')],
      ['Status', formatStatusLabel(inspection.status, 'Pending')],
      ['Location', toSingleLine(locationAddress, 'Location not available', 48)],
    ];

    const detailRowCount = Math.max(detailsLeft.length, detailsRight.length);
    const detailRowHeight = 28;
    const detailBoxHeight = 30 + detailRowCount * detailRowHeight;
    const midX = margin + contentWidth / 2;
    ensureSpace(detailBoxHeight + 16, 'Inspection Executive Summary');

    doc.setDrawColor(...palette.border);
    doc.setLineWidth(0.8);
    doc.rect(margin, y, contentWidth, detailBoxHeight);
    doc.setFillColor(250, 251, 253);
    doc.rect(margin + 1, y + 1, contentWidth - 2, 28, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...palette.dark);
    doc.text('Field', margin + 10, y + 19);
    doc.text('Value', margin + 110, y + 19);
    doc.text('Field', midX + 10, y + 19);
    doc.text('Value', midX + 110, y + 19);

    doc.setDrawColor(...palette.border);
    doc.line(midX, y, midX, y + detailBoxHeight);

    for (let index = 0; index < detailRowCount; index += 1) {
      const rowY = y + 30 + index * detailRowHeight;
      if (index < detailRowCount - 1) {
        doc.line(margin, rowY + detailRowHeight, pageWidth - margin, rowY + detailRowHeight);
      }

      const [leftLabel, leftValue] = detailsLeft[index] || ['', ''];
      const [rightLabel, rightValue] = detailsRight[index] || ['', ''];

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...palette.muted);
      doc.text(leftLabel, margin + 10, rowY + 11);
      doc.text(rightLabel, midX + 10, rowY + 11);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(...palette.dark);
      doc.text(toDisplayText(leftValue), margin + 110, rowY + 21);
      doc.text(toDisplayText(rightValue), midX + 110, rowY + 21);
    }

    y += detailBoxHeight + 16;

    drawSectionHeading('Additional Comments');

    const notesText = toDisplayText(
      inspection.notes || inspection.comments,
      'No additional comments were provided for this inspection.'
    );
    const noteLines = doc.splitTextToSize(notesText, contentWidth - 24);
    const notesBoxHeight = Math.max(56, noteLines.length * 12 + 20);
    ensureSpace(notesBoxHeight + 14, 'Inspection Executive Summary');

    doc.setDrawColor(...palette.border);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, contentWidth, notesBoxHeight, 6, 6, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...palette.dark);
    doc.text(noteLines, margin + 12, y + 18);
    y += notesBoxHeight + 18;

    if (flaggedItems.length > 0) {
      const drawFlaggedHeading = (isContinuation = false) => {
        drawSectionHeading(
          isContinuation
            ? 'Flagged Items & Corrective Actions (Continued)'
            : 'Flagged Items & Corrective Actions',
          `${flaggedItems.length} flagged item(s) - ${actionsCount} action(s)`
        );
      };

      drawFlaggedHeading(false);

      flaggedItems.forEach((item, index) => {
        const priorityColor = getPriorityColor(item.priority);
        const questionNumber = String(item.key).replace('q', '');
        const questionLines = doc.splitTextToSize(item.question, contentWidth - 165);
        const actionLine = [
          `Assignee: ${preparedBy}`,
          `Priority: ${item.priority}`,
          `Due: ${formatDateTime(actionDueDate)}`,
          `Created by: ${preparedBy}`,
        ].join(' | ');
        const actionLines = doc.splitTextToSize(actionLine, contentWidth - 24);
        const commentLines = item.comment
          ? doc.splitTextToSize(`Note: ${item.comment}`, contentWidth - 24)
          : [];

        const cardHeight =
          20 +
          questionLines.length * 12 +
          12 +
          actionLines.length * 11 +
          (commentLines.length > 0 ? 10 + commentLines.length * 11 : 0) +
          14;

        ensureSpace(cardHeight + 12, 'Flagged Items & Corrective Actions', () => {
          drawFlaggedHeading(true);
        });

        doc.setDrawColor(...palette.border);
        doc.setFillColor(255, 252, 247);
        doc.roundedRect(margin, y, contentWidth, cardHeight, 6, 6, 'FD');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...palette.muted);
        doc.text(`Item ${index + 1} - Q${questionNumber}`, margin + 12, y + 16);

        const failBadgeWidth = drawRightBadge({
          rightX: pageWidth - margin - 12,
          y: y + 8,
          label: 'FAIL',
          fillColor: palette.danger,
        });

        drawRightBadge({
          rightX: pageWidth - margin - 20 - failBadgeWidth,
          y: y + 8,
          label: item.priority.toUpperCase(),
          fillColor: priorityColor,
        });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...palette.dark);
        doc.text(questionLines, margin + 12, y + 34);

        const actionY = y + 36 + questionLines.length * 12;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...palette.dark);
        doc.text(actionLines, margin + 12, actionY + 12);

        if (commentLines.length > 0) {
          const commentsY = actionY + actionLines.length * 11 + 14;
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(9);
          doc.setTextColor(...palette.muted);
          doc.text(commentLines, margin + 12, commentsY);
        }

        y += cardHeight + 12;
      });
    }

    startPage('Inspection Checklist');

    const columnWidths = {
      number: 34,
      item: 252,
      result: 72,
      priority: 78,
    };
    columnWidths.note =
      contentWidth -
      columnWidths.number -
      columnWidths.item -
      columnWidths.result -
      columnWidths.priority;

    const columnX = {
      number: margin,
      item: margin + columnWidths.number,
      result: margin + columnWidths.number + columnWidths.item,
      priority:
        margin +
        columnWidths.number +
        columnWidths.item +
        columnWidths.result,
      note:
        margin +
        columnWidths.number +
        columnWidths.item +
        columnWidths.result +
        columnWidths.priority,
    };

    const drawChecklistHeader = (isContinuation = false) => {
      drawSectionHeading(
        isContinuation ? 'Inspection Checklist (Continued)' : 'Inspection Checklist',
        `${totalQuestions} checks`
      );

      const headerHeight = 24;
      doc.setFillColor(242, 245, 249);
      doc.setDrawColor(...palette.border);
      doc.rect(margin, y, contentWidth, headerHeight, 'FD');

      doc.line(columnX.item, y, columnX.item, y + headerHeight);
      doc.line(columnX.result, y, columnX.result, y + headerHeight);
      doc.line(columnX.priority, y, columnX.priority, y + headerHeight);
      doc.line(columnX.note, y, columnX.note, y + headerHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...palette.dark);
      doc.text('#', columnX.number + columnWidths.number / 2, y + 16, { align: 'center' });
      doc.text('Checklist Item', columnX.item + 8, y + 16);
      doc.text('Result', columnX.result + columnWidths.result / 2, y + 16, {
        align: 'center',
      });
      doc.text('Priority', columnX.priority + columnWidths.priority / 2, y + 16, {
        align: 'center',
      });
      doc.text('Notes / Actions', columnX.note + 8, y + 16);

      y += headerHeight;
    };

    drawChecklistHeader(false);

    analyzedResults.forEach((item, index) => {
      const itemLines = doc.splitTextToSize(item.question, columnWidths.item - 12);
      const noteText =
        item.result === 'fail'
          ? toDisplayText(item.comment, 'Action required - no note provided')
          : toDisplayText(item.comment, '-');
      const noteLines = doc.splitTextToSize(noteText, columnWidths.note - 12);
      const rowLineCount = Math.max(itemLines.length, noteLines.length, 1);
      const rowHeight = Math.max(26, rowLineCount * 11 + 10);

      if (y + rowHeight > bodyBottom) {
        startPage('Inspection Checklist');
        drawChecklistHeader(true);
      }

      doc.setDrawColor(...palette.border);
      doc.rect(margin, y, contentWidth, rowHeight);
      doc.line(columnX.item, y, columnX.item, y + rowHeight);
      doc.line(columnX.result, y, columnX.result, y + rowHeight);
      doc.line(columnX.priority, y, columnX.priority, y + rowHeight);
      doc.line(columnX.note, y, columnX.note, y + rowHeight);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...palette.muted);
      doc.text(String(index + 1), columnX.number + columnWidths.number / 2, y + 15, {
        align: 'center',
      });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...palette.dark);
      doc.text(itemLines, columnX.item + 6, y + 14);

      const resultLabel =
        item.result === 'pass' ? 'PASS' : item.result === 'fail' ? 'FAIL' : 'N/A';
      drawCenteredBadge({
        x: columnX.result,
        y: y + (rowHeight - 24) / 2,
        width: columnWidths.result,
        label: resultLabel,
        tone: item.result,
      });

      if (item.result === 'fail') {
        const priorityTone =
          String(item.priority).toLowerCase() === 'high'
            ? 'fail'
            : String(item.priority).toLowerCase() === 'medium'
              ? 'priority'
              : 'pass';

        drawCenteredBadge({
          x: columnX.priority,
          y: y + (rowHeight - 24) / 2,
          width: columnWidths.priority,
          label: item.priority.toUpperCase(),
          tone: priorityTone,
        });
      } else {
        drawCenteredBadge({
          x: columnX.priority,
          y: y + (rowHeight - 24) / 2,
          width: columnWidths.priority,
          label: '-',
          tone: 'na',
        });
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...palette.dark);
      doc.text(noteLines, columnX.note + 6, y + 14);

      y += rowHeight;
    });

    ensureSpace(110, 'Inspection Checklist');
    doc.setDrawColor(...palette.border);
    doc.line(margin, y + 10, pageWidth - margin, y + 10);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...palette.dark);
    doc.text('Report Sign-off', margin, y + 30);

    const signOffBoxY = y + 38;
    const signOffBoxHeight = 68;
    const signOffMidX = margin + contentWidth / 2;

    doc.setDrawColor(...palette.border);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, signOffBoxY, contentWidth, signOffBoxHeight, 6, 6, 'FD');
    doc.line(signOffMidX, signOffBoxY, signOffMidX, signOffBoxY + signOffBoxHeight);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...palette.primary);
    doc.text('Submitted (By Driver)', margin + 12, signOffBoxY + 18);
    doc.text('Report Completed', signOffMidX + 12, signOffBoxY + 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.8);
    doc.setTextColor(...palette.dark);
    doc.text(toSingleLine(preparedBy, 'Unspecified'), margin + 12, signOffBoxY + 34);
    doc.text(
      `Inspection date: ${formatDateTime(conductedAt, 'Not recorded')}`,
      margin + 12,
      signOffBoxY + 50
    );
    doc.text(`Approved by: ${toSingleLine(approvedBy, 'Not recorded')}`, signOffMidX + 12, signOffBoxY + 34);
    doc.text(
      `Date & time: ${formatDateTime(approvedAt, 'Not recorded')}`,
      signOffMidX + 12,
      signOffBoxY + 50
    );

    const pageCount = doc.internal.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      doc.setPage(pageNumber);

      const footerTop = pageHeight - margin - footerHeight + 4;
      doc.setDrawColor(...palette.border);
      doc.setLineWidth(0.8);
      doc.line(margin, footerTop, pageWidth - margin, footerTop);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...palette.muted);
      doc.text("R Ally's Transfers & Tours | Vehicle Inspection Report", margin, footerTop + 14);
      doc.text(
        `Page ${pageNumber} of ${pageCount}`,
        pageWidth - margin,
        footerTop + 14,
        { align: 'right' }
      );
    }

    const safeDriverName = toDisplayText(inspection.driverName, 'Driver')
      .replace(/\s+/g, '-')
      .replace(/[^A-Za-z0-9_-]/g, '');
    const fileName = `R-Allys-Inspection-Report-${safeDriverName || 'Driver'}-${formatDateForFileName(conductedAt)}.pdf`;
    doc.save(fileName);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DD2424' }}>
          Inspections
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={fetchInspections}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Has Failures"
              value={filterHasFailures}
              onChange={(e) => setFilterHasFailures(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Card sx={{ backgroundColor: '#f5f5f5', height: '100%' }}>
              <CardContent sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total: {filteredInspections.length} | 
                  Pending: {filteredInspections.filter(i => i.status === 'pending').length} | 
                  Failed: {filteredInspections.filter(i => i.hasFailures).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <Table>
          <TableHead sx={{ backgroundColor: '#DD2424' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & time</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Submitted (By Driver)</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Report Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Results</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Completed (by Admin)</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Issues Identified</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInspections
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((inspection) => (
                <TableRow key={inspection.id} hover>
                  <TableCell>{formatDate(inspection.createdAt)}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {inspection.driverName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {inspection.driverEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{inspection.inspectionType || 'N/A'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />
                      <Typography variant="body2">
                        {(inspection.totalQuestions || 0) - (inspection.failedCount || 0)}/{inspection.totalQuestions || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatusLabel(inspection.status, 'Pending')}
                      size="small"
                      color={
                        inspection.status === 'approved' ? 'success' :
                        inspection.status === 'rejected' ? 'error' : 'warning'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {inspection.hasFailures ? (
                      <Chip
                        icon={<Warning />}
                        label={`${inspection.failedCount} Failed`}
                        size="small"
                        color="error"
                      />
                    ) : (
                      <Chip label="None" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(inspection)}
                      sx={{ color: '#DD2424' }}
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => exportPdf(inspection)}
                      sx={{ color: '#4CAF50' }}
                    >
                      <GetApp />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredInspections.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ backgroundColor: '#DD2424', color: 'white' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Inspection Details</Typography>
                {selected.hasFailures && <Warning sx={{ color: '#FFC107' }} />}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Inspection Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Information</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Created (Date & Time)</Typography>
                    <Typography variant="body1">{formatDate(selected.createdAt)}</Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Last Updated (Date & Time)</Typography>
                    <Typography variant="body1">{selected.updatedAt ? formatDate(selected.updatedAt) : 'N/A'}</Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Driver Name</Typography>
                    <Typography variant="body1">{selected.driverName}</Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Driver ID</Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{selected.driverId}</Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selected.driverEmail}</Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Report Type</Typography>
                    <Typography variant="body1">{selected.inspectionType}</Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Vehicle</Typography>
                    <Typography variant="body1">{selected.vehicleName || selected.vehicleModel || 'N/A'} </Typography>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Vehicle ID</Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{selected.vehicleId || 'N/A'}</Typography>
                  </Grid>
                  {selected.vehiclePlate && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Vehicle Plate</Typography>
                      <Typography variant="body1">{selected.vehiclePlate}</Typography>
                    </Grid>
                  )}
                  {selected.location?.address && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">{selected.location.address}</Typography>
                      {selected.location.latitude && selected.location.longitude && (
                        <Typography variant="caption" color="text.secondary">
                          ({selected.location.latitude}, {selected.location.longitude})
                        </Typography>
                      )}
                    </Grid>
                  )}
                  {selected.conductedOn && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Conducted On</Typography>
                      <Typography variant="body1">{new Date(selected.conductedOn).toLocaleString()}</Typography>
                    </Grid>
                  )}
                  {selected.preparedBy && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Submitted (By Driver)</Typography>
                      <Typography variant="body1">{selected.preparedBy}</Typography>
                    </Grid>
                  )}
                  {selected.odometerReading && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Odometer Reading</Typography>
                      <Typography variant="body1">{selected.odometerReading}</Typography>
                    </Grid>
                  )}
                  {selected.completionPercentage !== undefined && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Completion</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: selected.completionPercentage === 100 ? '#4CAF50' : '#FFC107' }}>
                        {selected.completionPercentage}%
                      </Typography>
                    </Grid>
                  )}
                  <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                    <Typography variant="body2" color="text.secondary">Completed (by Admin)</Typography>
                    <Chip
                      label={formatStatusLabel(selected.status, 'Pending')}
                      size="small"
                      color={
                        selected.status === 'approved' ? 'success' :
                        selected.status === 'rejected' ? 'error' : 'warning'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Grid>
                  {(selected.status === 'approved' || selected.approvedBy) && (
                    <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
                      <Typography variant="body2" color="text.secondary">Approved By</Typography>
                      <Typography variant="body1">
                        {resolveInspectionApproverName(selected)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Inspection Results */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Results</Typography>
                <Divider sx={{ mb: 2 }} />
                {Object.entries(selected.inspectionResults || {}).map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: value === true ? '#E8F5E9' : value === false ? '#FFEBEE' : '#F5F5F5',
                      borderRadius: 1,
                      borderLeft: `4px solid ${value === true ? '#4CAF50' : value === false ? '#DD2424' : '#999'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      {value === true ? (
                        <CheckCircle sx={{ color: '#4CAF50', mt: 0.5 }} />
                      ) : value === false ? (
                        <Cancel sx={{ color: '#DD2424', mt: 0.5 }} />
                      ) : null}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {INSPECTION_QUESTIONS[key] || key}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 'bold',
                            color: value === true ? '#4CAF50' : value === false ? '#DD2424' : '#999',
                          }}
                        >
                          {value === true ? 'PASS' : value === false ? 'FAIL' : 'N/A'}
                        </Typography>
                        
                        {/* Display priority for failed items */}
                        {value === false && selected.failedItemPriority?.[key] && (
                          <Chip
                            label={selected.failedItemPriority[key].toUpperCase()}
                            size="small"
                            sx={{
                              ml: 1,
                              backgroundColor: 
                                selected.failedItemPriority[key] === 'high' ? '#DD2424' :
                                selected.failedItemPriority[key] === 'medium' ? '#FF9800' : '#4CAF50',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '10px',
                            }}
                          />
                        )}
                        
                        {/* Edit button for failed items */}
                        {value === false && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditOpen(key)}
                            sx={{ ml: 1, color: '#1976d2' }}
                            title="Update to Pass"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        
                        {/* Display admin update info if available */}
                        {selected.itemUpdates?.[key] && (
                          <Box sx={{ mt: 1, p: 1, backgroundColor: '#E3F2FD', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              Admin Update: Changed to PASS on {new Date(selected.itemUpdates[key].updateDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              Remark: {selected.itemUpdates[key].remark}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Display comments/notes if available */}
                        {selected.itemComments?.[key] && (
                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
                            Note: {selected.itemComments[key]}
                          </Typography>
                        )}
                        
                        {/* Display photo if available */}
                        {selected.itemPhotos?.[key] && (
                          <Box sx={{ mt: 1 }}>
                            <img
                              src={selected.itemPhotos[key]}
                              alt={`Inspection ${key}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: '8px',
                                marginTop: '8px',
                                cursor: 'pointer',
                              }}
                              onClick={() => window.open(selected.itemPhotos[key], '_blank')}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Summary */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Card sx={{ textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                      <CardContent>
                        <Typography variant="h4" color="primary">{selected.totalQuestions || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Total</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ textAlign: 'center', backgroundColor: '#E8F5E9' }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ color: '#4CAF50' }}>
                          {(selected.totalQuestions || 0) - (selected.failedCount || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Passed</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ textAlign: 'center', backgroundColor: '#FFEBEE' }}>
                      <CardContent>
                        <Typography variant="h4" sx={{ color: '#DD2424' }}>
                          {selected.failedCount || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Failed</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Notes */}
              {selected.notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Additional Notes</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="body2">{selected.notes}</Typography>
                  </Paper>
                </Box>
              )}

              {/* Warning */}
              {selected.hasFailures && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#FFF3CD', borderRadius: 1, borderLeft: '4px solid #FFC107' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning sx={{ color: '#DD2424' }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#DD2424' }}>
                      Issues Identified: This inspection contains {selected.failedCount} failed item(s)
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    Immediate attention and corrective action required.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => exportPdf(selected)} startIcon={<GetApp />} color="success">
                Download PDF
              </Button>
              {selected.status === 'pending' && (
                <>
                  <Button
                    onClick={() => updateInspectionStatus(selected.id, 'approved')}
                    color="success"
                    variant="contained"
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={() => updateInspectionStatus(selected.id, 'rejected')}
                    color="error"
                    variant="contained"
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Failed Item Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          Update Failed Item to Pass
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Question:
            </Typography>
            <Typography variant="body2" sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {editItem && INSPECTION_QUESTIONS[editItem]}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Update Date"
            type="date"
            value={updateDate}
            onChange={(e) => setUpdateDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Admin Remark"
            multiline
            rows={4}
            value={updateRemark}
            onChange={(e) => setUpdateRemark(e.target.value)}
            placeholder="Enter the reason for changing this item from Fail to Pass..."
            helperText="Required: Explain why this item is now passing"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button
            onClick={handleUpdateFailedItem}
            variant="contained"
            color="success"
            disabled={!updateDate || !updateRemark.trim()}
          >
            Update to Pass
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InspectionsList;
